import { CheckCircle } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/utils/format/date';
import { formatNumber } from '@/utils/format/number';
import type { ConditionViolation } from '@/warranty-v3/entities/condition-violation';

export type ConditionViolationsTableProps = {
  conditionViolations: ConditionViolation[];
};

function EmptyState() {
  return (
    <tr className="border border-green-100 bg-green-50 text-green-800 shadow-inner">
      <td colSpan={6} className="p-6 text-center align-middle">
        <div className="flex flex-col items-center justify-center gap-2">
          <CheckCircle className="size-6 text-green-600" />
          <div className="text-lg font-semibold">
            No condition violation records detected
          </div>
          <div className="text-sm text-gray-600">
            All systems are operating within warranty conditions.
          </div>
        </div>
      </td>
    </tr>
  );
}

export function ConditionViolationsTable({
  conditionViolations,
}: ConditionViolationsTableProps) {
  return (
    <Table striped dense bleed className="w-full table-auto overflow-hidden">
      <TableHead className="sticky top-0 bg-gray-100">
        <TableRow>
          <TableHeader className="text-left align-bottom">
            Description
          </TableHeader>
          <TableHeader narrow className="text-left align-bottom">
            Extreme value
          </TableHeader>
          <TableHeader narrow className="text-left align-bottom">
            Latest Detect.
          </TableHeader>
          <TableHeader narrow className="text-left align-bottom">
            Duration
          </TableHeader>
          <TableHeader narrow className="text-left align-bottom">
            Affected Units
          </TableHeader>
        </TableRow>
      </TableHead>

      <TableBody>
        {conditionViolations.length === 0 ? (
          <EmptyState />
        ) : (
          conditionViolations.map((violation) => (
            <TableRow key={violation.id} className="hover:bg-gray-50">
              <TableCell wide justify="left">
                {violation.description}
              </TableCell>
              <TableCell narrow justify="left">
                {formatNumber(violation.extremeValue, 1)}{' '}
                {violation.extremeValueUnit}
              </TableCell>
              <TableCell narrow justify="left">
                {formatDate(violation.lastDetectedAt)}
              </TableCell>
              <TableCell narrow justify="left">
                {formatNumber(violation.durationHours, 1)} h
              </TableCell>
              <TableCell narrow justify="left">
                {violation.affectedUnitsCount}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
